import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { SermonSeries } from '../../shared/api/types';
import { Alert, Button, Card, Field } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

/** Video (YouTube) y serie de una predicación o estudio. Se ve de inmediato en lo publicado. */
export function PieceMediaCard({ churchId, pieceId, canEdit }: { churchId: string; pieceId: string; canEdit: boolean }) {
  const base = `/content/churches/${churchId}`;
  const data = useAsync(
    async (signal) => {
      const [media, series] = await Promise.all([
        api.get<{ videoUrl: string | null; seriesId: string | null }>(`${base}/pieces/${pieceId}/media`, signal),
        api.get<{ items: SermonSeries[] }>(`${base}/series`, signal),
      ]);
      return { ...media, series: series.items };
    },
    [base, pieceId],
  );
  const [videoUrl, setVideoUrl] = useState('');
  const [seriesId, setSeriesId] = useState('');
  const [newSeries, setNewSeries] = useState('');
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const loaded = data.status === 'ready' ? data.data : null;
  useEffect(() => {
    if (loaded) {
      setVideoUrl(loaded.videoUrl ?? '');
      setSeriesId(loaded.seriesId ?? '');
    }
  }, [loaded]);
  if (!loaded) return null;

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      let sid = seriesId || null;
      if (newSeries.trim()) sid = (await api.post<{ id: string }>(`${base}/series`, { title: newSeries.trim() })).id;
      await api.put(`${base}/pieces/${pieceId}/media`, { videoUrl: videoUrl.trim() || null, seriesId: sid });
      setNewSeries('');
      setMsg({ tone: 'success', text: 'Video y serie guardados.' });
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Video y serie">
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <Field label="Video (YouTube)" type="url" value={videoUrl} disabled={!canEdit} maxLength={300} placeholder="https://youtu.be/…" onChange={(e) => setVideoUrl(e.target.value)} />
      <div className="form__row">
        <div className="field">
          <label htmlFor="piece-series">Serie</label>
          <select id="piece-series" value={seriesId} disabled={!canEdit} onChange={(e) => setSeriesId(e.target.value)}>
            <option value="">Sin serie</option>
            {loaded.series.filter((s) => !s.archived || s.id === seriesId).map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </div>
        {canEdit ? <Field label="O crea una serie nueva" value={newSeries} maxLength={120} placeholder="Fe que obra" onChange={(e) => setNewSeries(e.target.value)} /> : null}
      </div>
      {canEdit ? (
        <Button busy={busy} onClick={() => void save()}>
          Guardar video y serie
        </Button>
      ) : null}
    </Card>
  );
}
